import qs from 'qs';
import axios, { AxiosResponse } from 'axios';

import { createTrendingPostsFromRawPosts } from '@utils/common.utils';

import {
  BlogHomepageCardDetailsType,
  HeroSectionHeadingResponseType,
  BlogCardProps,
  TrendingListResponseType,
  QueryType,
  BlogTypeDetailsType,
  BlogTypeDetailsResponse,
} from 'src/types';

const API = axios.create();

export { API };

async function fetchHomepageHeroSectionTitle(
  url: '/blog-homepage-heading',
  language = 'en',
): Promise<HeroSectionHeadingResponseType> {
  const titleURL = `${process.env.NEXT_PUBLIC_STRAPI_API_URL}${url}?_locale=${language}`;

  const { data }: AxiosResponse<HeroSectionHeadingResponseType> = await API.get(
    titleURL,
  );

  return data;
}

async function fetchAllProfessions(
  query?: QueryType,
): Promise<BlogHomepageCardDetailsType[]> {
  let professionURL = `${process.env.NEXT_PUBLIC_STRAPI_API_URL}/professions?`;

  const queryData: Record<string, any> = {
    _sort: 'order:ASC',
    _locale: query?.language || 'en',
  };

  if (query) {
    queryData['slug_ne'] = query.listTypeToSkip;
  }

  professionURL += qs.stringify(queryData, { encode: false });

  const { data }: AxiosResponse<BlogHomepageCardDetailsType[]> = await API.get(
    professionURL,
  );
  return data.map(({ id, name, description, slug, image, SEO, bgColor }) => {
    return { id, name, description, slug, image, SEO, bgColor };
  });
}

async function fetchAllTrendingList({
  limit = 10,
  language = 'EN',
  start = 0,
}: QueryType): Promise<BlogCardProps[]> {
  let trendingListURL = `${process.env.NEXT_PUBLIC_STRAPI_API_URL}/blogs?`;

  const queryData = {
    _start: start,
    _limit: limit,
    trending: true,
    _locale: language,
    _sort: ['TrendingPostOrder:ASC', 'published_at:DESC'],
    _where: {
      1: {
        Language: language.toUpperCase(),
      },
      2: {
        TrendingPostOrder_null: 0,
      },
    },
  };

  trendingListURL += qs.stringify(queryData, {
    encode: false,
    arrayFormat: 'comma',
  });

  const { data }: AxiosResponse<TrendingListResponseType[]> = await API.get(
    trendingListURL,
  );

  return data.map(data => {
    return {
      id: data.id,
      title: data.Title,
      featureImage: data.FeatureImage,
      slug: data.Slug,
      trending: data.trending,
      category: data.categories_v_2,
      platform: data.platform,
      profession: data.profession,
    };
  });
}

async function fetchProfessionDetails(
  slug: string,
  language = 'en',
): Promise<BlogTypeDetailsType> {
  let professionURL = `${process.env.NEXT_PUBLIC_STRAPI_API_URL}/professions/${slug}?_locale=${language}`;

  const { data }: AxiosResponse<BlogTypeDetailsResponse> = await API.get(
    professionURL,
  );

  const { id, name, description, image, trendingPosts, SEO, bgColor } = data;

  return {
    id,
    name,
    description,
    image,
    slug,
    trendingPosts: createTrendingPostsFromRawPosts(trendingPosts),
    SEO,
    bgColor,
  };
}

async function fetchTotalTrendingPostsCount({
  language = 'EN',
}): Promise<number> {
  let url = `${process.env.NEXT_PUBLIC_STRAPI_API_URL}/blogs/count?`;

  const queryData = {
    trending: true,
    _locale: language.toLowerCase(),
    _where: {
      1: {
        Language: language.toUpperCase(),
      },
      2: {
        TrendingPostOrder_null: 0,
      },
    },
  };

  url += qs.stringify(queryData, { encode: false });

  try {
    const { data }: AxiosResponse<number> = await API.get(url);

    return data;
  } catch (error) {
    console.error(error);

    return 0;
  }
}

export {
  fetchAllProfessions,
  fetchHomepageHeroSectionTitle,
  fetchAllTrendingList,
  fetchProfessionDetails,
  fetchTotalTrendingPostsCount,
};
